import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Wifi,
  Speed,
  Security,
  CheckCircle,
  ArrowForward,
  Router,
  Storage,
  Cloud,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const InternetSolutions = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const solutions = [
    { name: 'Dedicated Internet Access', speed: '100Mbps - 100Gbps' },
    { name: 'Broadband Solutions', speed: '50Mbps - 1Gbps' },
    { name: 'SD-WAN Services', speed: 'Customizable' },
    { name: '4G/5G Backup', speed: 'Up to 1Gbps' },
  ];

  return (
    <Box>
      <PageHeader 
        title="Internet Solutions" 
        subtitle="High-speed connectivity for businesses and enterprises"
      />

      {/* Solutions Grid */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Internet Solutions" 
                  sx={{ mb: 2, bgcolor: theme.palette.primary.light + '20', color: theme.palette.primary.main }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                  Internet Solutions
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop"
                  alt="Internet Solutions"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: theme.shadows[8],
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Table */}
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 700 }}>
              Choose Your Solution
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              Find the perfect internet solution for your business
            </Typography>
          </motion.div>

          <Grid container spacing={3}>
            {solutions.map((solution, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                    <Wifi sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {solution.name}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ my: 2 }}>
                      {solution.speed}
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 3 }}
                      onClick={() => navigate('/contact')}
                    >
                      Get Started
                    </Button>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Get Connected" 
        subtitle="Find the perfect internet solution for your business"
        buttonText="Check Availability"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default InternetSolutions;